/**
 * YayCallBanner — Global floating call notification banner
 *
 * Mounted in App.jsx for all staff users. Listens to Socket.IO
 * 'yay:call-event' events and displays a stacked notification
 * with customer info and action buttons.
 *
 * Props:
 *   - socket: Socket.IO client instance
 *   - isStaff: boolean — only render for staff
 *   - notificationsEnabled: boolean — respect user toggle
 *   - onNewOrder(accountNumber): navigate to Order Entry for this customer
 *   - onSalesLedger(accountNumber): navigate to Sales Ledger
 *   - onSalesActivity(accountNumber): navigate to Sales Activity
 *   - onPayments(accountNumber): navigate to Customer Payments
 *   - onSearchCustomer(phone): open customer search for unknown callers
 *   - isDark: boolean
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

// ─── Feature-Code Shortcodes ──────────────────────────────────

// *30/*31/*32<ext> = listen/whisper/barge (admin supervision) — never shown.
const LISTEN_SHORTCODE_RE = /^\*3[0-2]\d*$/;
// *11 – *15 = call-park slots.
const PARK_SLOT_RE = /^\*1[1-5]$/;

function isListenShortcode(value) {
  return typeof value === 'string' && LISTEN_SHORTCODE_RE.test(value.trim());
}
function isParkSlot(value) {
  return typeof value === 'string' && PARK_SLOT_RE.test(value.trim());
}
function isListenEvent(event) {
  return Boolean(event?.isListenCall)
    || isListenShortcode(event?.to)
    || isListenShortcode(event?.from)
    || isListenShortcode(event?.answeredBy);
}
function isParkEvent(event) {
  return Boolean(event?.isParkedCall) || isParkSlot(event?.to);
}

// ─── Styles ───────────────────────────────────────────────────

const BANNER_STYLES = {
  container: {
    position: 'fixed',
    top: 60,
    right: 20,
    zIndex: 9999,
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    maxWidth: 420,
    pointerEvents: 'none',
  },
  banner: (isDark) => ({
    pointerEvents: 'auto',
    background: isDark ? '#1e293b' : '#ffffff',
    border: `2px solid ${isDark ? '#3b82f6' : '#2563eb'}`,
    borderRadius: 12,
    padding: '12px 16px',
    boxShadow: isDark
      ? '0 8px 32px rgba(0,0,0,0.6)'
      : '0 8px 32px rgba(0,0,0,0.15)',
    color: isDark ? '#e2e8f0' : '#1e293b',
    fontFamily: 'system-ui, -apple-system, sans-serif',
    fontSize: 14,
    animation: 'yaySlideIn 0.3s ease-out',
    transition: 'opacity 0.5s ease-out',
  }),
  bannerFading: {
    opacity: 0.4,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    marginBottom: 6,
  },
  icon: {
    fontSize: 18,
    marginRight: 6,
  },
  title: {
    fontWeight: 600,
    fontSize: 13,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    opacity: 0.7,
  },
  dismiss: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: 18,
    opacity: 0.6,
    padding: '0 4px',
    lineHeight: 1,
  },
  customerName: {
    fontWeight: 700,
    fontSize: 16,
    marginBottom: 2,
  },
  phone: {
    fontSize: 14,
    opacity: 0.8,
    marginBottom: 8,
  },
  duration: {
    fontSize: 12,
    opacity: 0.6,
    fontVariantNumeric: 'tabular-nums',
  },
  actions: {
    display: 'flex',
    gap: 6,
    flexWrap: 'wrap',
    marginTop: 8,
  },
  actionBtn: (isDark) => ({
    padding: '6px 12px',
    borderRadius: 6,
    border: `1px solid ${isDark ? '#475569' : '#cbd5e1'}`,
    background: isDark ? '#334155' : '#f1f5f9',
    color: isDark ? '#e2e8f0' : '#1e293b',
    cursor: 'pointer',
    fontSize: 12,
    fontWeight: 500,
    transition: 'background 0.15s',
  }),
  voicemailText: {
    fontSize: 13,
    fontStyle: 'italic',
    opacity: 0.8,
    marginTop: 4,
    padding: '6px 8px',
    borderRadius: 6,
    background: 'rgba(0,0,0,0.05)',
    maxHeight: 60,
    overflow: 'hidden',
  },
  accountPicker: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    marginTop: 6,
  },
  accountPickerLabel: {
    fontSize: 12,
    fontWeight: 600,
    opacity: 0.7,
    marginBottom: 2,
  },
  accountBtn: (isDark) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 12px',
    borderRadius: 8,
    border: `1px solid ${isDark ? '#475569' : '#cbd5e1'}`,
    background: isDark ? '#1e293b' : '#f8fafc',
    color: isDark ? '#e2e8f0' : '#1e293b',
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 500,
    transition: 'background 0.15s, border-color 0.15s',
    textAlign: 'left',
    width: '100%',
  }),
  counter: {
    fontSize: 11,
    opacity: 0.5,
    textAlign: 'right',
    marginTop: 4,
  },
};

// ─── CSS animation (injected once) ───────────────────────────

const ANIMATION_CSS = `
@keyframes yaySlideIn {
  from { transform: translateX(100%); opacity: 0; }
  to   { transform: translateX(0);    opacity: 1; }
}
`;

let animationInjected = false;
function injectAnimation() {
  if (animationInjected || typeof document === 'undefined') return;
  const style = document.createElement('style');
  style.textContent = ANIMATION_CSS;
  document.head.appendChild(style);
  animationInjected = true;
}

// ─── Helpers ──────────────────────────────────────────────────

function formatDuration(seconds) {
  if (!seconds || seconds <= 0) return '';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function getEventIcon(eventType, direction) {
  if (eventType === 'voicemail') return '\uD83D\uDCEC'; // 📬
  if (eventType === 'ended') return '\uD83D\uDCDE'; // 📞 (grey-ish)
  if (direction === 'outbound') return '\uD83D\uDCDE'; // 📞
  return '\uD83D\uDCDE'; // 📞
}

function getEventTitle(eventType, direction) {
  if (eventType === 'voicemail') return 'NEW VOICEMAIL';
  if (eventType === 'ended') return 'CALL ENDED';
  if (eventType === 'answered') {
    return direction === 'outbound' ? 'ON CALL WITH' : 'ON CALL WITH';
  }
  // new_call
  return direction === 'outbound' ? 'CALLING OUT TO' : 'INCOMING CALL FROM';
}

// ─── Component ────────────────────────────────────────────────

const MAX_VISIBLE = 3;
const AUTO_DISMISS_MS = {
  ended: 8000,
  voicemail: 30000,
};

export default function YayCallBanner({
  socket,
  isStaff = false,
  notificationsEnabled = true,
  userKey = null,
  onNewOrder,
  onSalesLedger,
  onSalesActivity,
  onPayments,
  onSearchCustomer,
  isDark = false,
}) {
  const [calls, setCalls] = useState([]); // array of call event objects
  const [selectedAccounts, setSelectedAccounts] = useState({}); // callId → chosen customer object
  const timersRef = useRef({}); // callId → timeout handle

  useEffect(() => {
    injectAnimation();
  }, []);

  // ─── Socket listener ─────────────────────────────────────

  useEffect(() => {
    if (!socket || !isStaff || !notificationsEnabled) return;

    // Resolve the real party behind a call-park pickup and merge it into the
    // banner (server only sends the "*11" slot in the webhook payload).
    const enrichParkedCall = (event) => {
      const params = {};
      if (event.parentCallId) params.parentUuid = event.parentCallId;
      if (event.callId) params.uuid = event.callId;
      if (event.from) params.from = event.from;
      if (event.answeredBy) params.answeredBy = event.answeredBy;
      if (!params.parentUuid && !params.uuid && !params.from && !params.answeredBy) return;
      axios.get('/api/yay/parked-party', { params })
        .then(({ data }) => {
          const party = data?.party;
          if (!party) return;
          setCalls((prev) => prev.map((c) => {
            if (c.callId !== event.callId) return c;
            const customer = party.accountNumber
              ? { accountNumber: party.accountNumber, customerName: party.customerName }
              : c.matchedCustomer;
            return {
              ...c,
              matchedCustomer: customer,
              matchedCustomers: customer ? [customer] : c.matchedCustomers,
              externalNumber: party.number || c.externalNumber,
              externalNumberFormatted: party.numberFormatted || party.number || c.externalNumberFormatted,
            };
          }));
        })
        .catch((err) => console.error('[Yay] parked-party lookup failed:', err));
    };

    const handleCallEvent = (event) => {
      if (!event?.callId) return;

      // Silent-monitoring calls (*30/*31/*32<ext> = listen/whisper/barge) are
      // admin supervision tools — never surface a banner for them.
      if (isListenEvent(event)) return;

      // Call-park pickup (*11 – *15): the slot hides the real caller. Resolve
      // the parked party from the original call leg and show them instead.
      if (isParkEvent(event)) {
        enrichParkedCall(event);
      }

      setCalls((prev) => {
        // For answered/ended: update existing call if we have it
        const existingIdx = prev.findIndex(
          (c) => c.callId === event.callId || c.callId === event.parentCallId
        );

        if (existingIdx >= 0) {
          const updated = [...prev];
          updated[existingIdx] = {
            ...updated[existingIdx],
            ...event,
            // Keep the customer info if the new event doesn't have it
            matchedCustomer: event.matchedCustomer || updated[existingIdx].matchedCustomer,
            matchedCustomers: event.matchedCustomers || updated[existingIdx].matchedCustomers,
            multipleMatches: event.multipleMatches ?? updated[existingIdx].multipleMatches,
          };
          return updated;
        }

        // Suppress new incoming call banners when user is already on an active call
        const userOnActiveCall = prev.some(
          (c) => c.eventType === 'answered' &&
                 (!c.answeredByUserKey || c.answeredByUserKey === userKey)
        );
        if (userOnActiveCall && event.eventType === 'new_call') {
          return prev; // Don't distract with new banners mid-call
        }

        // New call — add to top
        return [event, ...prev];
      });

      // Auto-dismiss for ended calls (and "answered by someone else")
      let dismissMs = AUTO_DISMISS_MS[event.eventType];

      // If someone else answered this call, auto-dismiss for other users
      if (event.eventType === 'answered' && event.answeredByUserKey && event.answeredByUserKey !== userKey) {
        dismissMs = 5000;
      }

      if (dismissMs) {
        const id = event.callId;
        // Clear existing timer if any
        if (timersRef.current[id]) clearTimeout(timersRef.current[id]);
        timersRef.current[id] = setTimeout(() => {
          setCalls((prev) => prev.filter((c) => c.callId !== id));
          delete timersRef.current[id];
        }, dismissMs);
      }
    };

    socket.on('yay:call-event', handleCallEvent);
    return () => {
      socket.off('yay:call-event', handleCallEvent);
    };
  }, [socket, isStaff, notificationsEnabled, userKey]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      Object.values(timersRef.current).forEach(clearTimeout);
    };
  }, []);

  // ─── Dismiss handler ─────────────────────────────────────

  const dismiss = useCallback((callId) => {
    setCalls((prev) => prev.filter((c) => c.callId !== callId));
    if (timersRef.current[callId]) {
      clearTimeout(timersRef.current[callId]);
      delete timersRef.current[callId];
    }
  }, []);

  // ─── Account selection (multi-match) ──────────────────────

  const handleSelectAccount = useCallback((callId, customer) => {
    setSelectedAccounts((prev) => ({ ...prev, [callId]: customer }));
  }, []);

  // ─── Manual customer link ────────────────────────────────

  const handleLinkCustomer = useCallback(async (callId, accountNumber, customerName, phone) => {
    try {
      await axios.post('/api/yay/phone-mappings', {
        accountNumber,
        customerName,
        phone,
        label: 'Manual',
      });

      // Update the call in state — add to matchedCustomers so user can switch accounts
      setCalls((prev) =>
        prev.map((c) => {
          if (c.callId !== callId) return c;
          const existing = c.matchedCustomers || [];
          // If this customer isn't already in the list, add it
          const alreadyExists = existing.some((m) => m.accountNumber === accountNumber);
          const updatedCustomers = alreadyExists
            ? existing
            : [...existing, { accountNumber, customerName }];
          // If there was only a single matchedCustomer (no array), include it too
          if (c.matchedCustomer && !existing.some((m) => m.accountNumber === c.matchedCustomer.accountNumber)) {
            updatedCustomers.unshift(c.matchedCustomer);
          }
          return {
            ...c,
            matchedCustomers: updatedCustomers,
            multipleMatches: updatedCustomers.length > 1,
            // Keep matchedCustomer as the newly linked one for immediate display
            matchedCustomer: { accountNumber, customerName },
          };
        })
      );
    } catch (err) {
      console.error('Failed to save phone mapping:', err);
    }
  }, []);

  // ─── Save non-customer contact (name only, no account) ───

  const [savingContact, setSavingContact] = useState({}); // callId → 'input' | 'saving' | 'done'
  const [contactNameInput, setContactNameInput] = useState({}); // callId → string

  const handleSaveContact = useCallback(async (callId, phone) => {
    const name = (contactNameInput[callId] || '').trim();
    if (!name) return;
    setSavingContact((prev) => ({ ...prev, [callId]: 'saving' }));
    try {
      await axios.post('/api/yay/phone-mappings', {
        contactName: name,
        phone,
        label: 'Contact',
      });
      // Update call in state with the new contact name
      setCalls((prev) =>
        prev.map((c) => {
          if (c.callId !== callId) return c;
          return { ...c, contactName: name };
        })
      );
      setSavingContact((prev) => ({ ...prev, [callId]: 'done' }));
    } catch (err) {
      console.error('Failed to save contact:', err);
      setSavingContact((prev) => ({ ...prev, [callId]: 'input' }));
    }
  }, [contactNameInput]);

  // ─── Render ──────────────────────────────────────────────

  if (!isStaff || !notificationsEnabled || calls.length === 0) return null;

  const visibleCalls = calls.slice(0, MAX_VISIBLE);
  const hiddenCount = Math.max(0, calls.length - MAX_VISIBLE);

  return (
    <div style={BANNER_STYLES.container}>
      {visibleCalls.map((call) => {
        // Resolve effective customer: user selection > single auto-match > null
        const customers = call.matchedCustomers || [];
        const hasMultiple = customers.length > 1;
        const selected = selectedAccounts[call.callId];
        const customer = selected || (customers.length === 1 ? customers[0] : null) || call.matchedCustomer;

        const isEnded = call.eventType === 'ended';
        const isVoicemail = call.eventType === 'voicemail';
        const isAnswered = call.eventType === 'answered';

        // Show action buttons only if: (a) call is answered/ended/voicemail AND
        // (b) this user is the one who answered (or it's a direct/outbound call with no answerer tracking)
        const isMyCall = !call.answeredByUserKey || call.answeredByUserKey === userKey;
        const showActions = (isAnswered || isVoicemail || isEnded) && isMyCall;

        // If someone else answered, show who answered
        const answeredByOther = isAnswered && call.answeredByUserKey && call.answeredByUserKey !== userKey;

        return (
          <div
            key={call.callId}
            style={{
              ...BANNER_STYLES.banner(isDark),
              ...(isEnded ? BANNER_STYLES.bannerFading : {}),
            }}
          >
            {/* Header */}
            <div style={BANNER_STYLES.header}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <span style={BANNER_STYLES.icon}>
                  {getEventIcon(call.eventType, call.direction)}
                </span>
                <span style={BANNER_STYLES.title}>
                  {getEventTitle(call.eventType, call.direction)}
                </span>
              </div>
              <button
                style={{ ...BANNER_STYLES.dismiss, color: isDark ? '#94a3b8' : '#64748b' }}
                onClick={() => dismiss(call.callId)}
                title="Dismiss"
              >
                ×
              </button>
            </div>

            {/* Customer info */}
            {customer ? (
              <>
                <div style={BANNER_STYLES.customerName}>
                  {customer.customerName || 'Unknown'}{' '}
                  <span style={{ fontWeight: 400, fontSize: 13, opacity: 0.7 }}>
                    ({customer.accountNumber})
                  </span>
                  {/* Show change button if user selected from multi-match */}
                  {hasMultiple && selected && (
                    <button
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: 11,
                        opacity: 0.6,
                        marginLeft: 6,
                        textDecoration: 'underline',
                        color: 'inherit',
                      }}
                      onClick={() =>
                        setSelectedAccounts((prev) => {
                          const next = { ...prev };
                          delete next[call.callId];
                          return next;
                        })
                      }
                      title="Choose a different account"
                    >
                      change
                    </button>
                  )}
                  {/* "+" button to link this same number to another customer */}
                  {onSearchCustomer && (
                    <button
                      style={{
                        background: 'none',
                        border: '1px solid ' + (isDark ? '#475569' : '#cbd5e1'),
                        borderRadius: 4,
                        cursor: 'pointer',
                        fontSize: 13,
                        fontWeight: 700,
                        marginLeft: 6,
                        padding: '0 5px',
                        lineHeight: '18px',
                        color: isDark ? '#94a3b8' : '#64748b',
                        verticalAlign: 'middle',
                      }}
                      onClick={() =>
                        onSearchCustomer(
                          call.externalNumber || call.from,
                          call.callId,
                          handleLinkCustomer
                        )
                      }
                      title="Link this number to an additional customer"
                    >
                      +
                    </button>
                  )}
                </div>
                <div style={BANNER_STYLES.phone}>
                  {call.externalNumberFormatted || call.externalNumber || call.from}
                </div>
              </>
            ) : hasMultiple && !selected ? (
              /* Multiple matches — show account picker */
              <>
                <div style={BANNER_STYLES.phone}>
                  {call.externalNumberFormatted || call.externalNumber || call.from}
                </div>
                <div style={BANNER_STYLES.accountPicker}>
                  <div style={BANNER_STYLES.accountPickerLabel}>
                    {customers.length} accounts found — select one:
                  </div>
                  {customers.map((c) => (
                    <button
                      key={c.accountNumber}
                      style={BANNER_STYLES.accountBtn(isDark)}
                      onClick={() => handleSelectAccount(call.callId, c)}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = isDark ? '#334155' : '#e2e8f0';
                        e.currentTarget.style.borderColor = isDark ? '#3b82f6' : '#2563eb';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = isDark ? '#1e293b' : '#f8fafc';
                        e.currentTarget.style.borderColor = isDark ? '#475569' : '#cbd5e1';
                      }}
                    >
                      <span style={{ fontWeight: 600 }}>{c.customerName || 'Unknown'}</span>
                      <span style={{ opacity: 0.6, fontSize: 12 }}>{c.accountNumber}</span>
                    </button>
                  ))}
                </div>
              </>
            ) : call.contactName ? (
              /* Named non-customer contact (supplier, courier, etc.) */
              <>
                <div style={BANNER_STYLES.customerName}>{call.contactName}</div>
                <div style={{ ...BANNER_STYLES.phone, display: 'flex', alignItems: 'center', gap: 6 }}>
                  {call.externalNumberFormatted || call.externalNumber || call.from}
                  <span style={{ fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 4, background: isDark ? '#334155' : '#e2e8f0', color: isDark ? '#94a3b8' : '#64748b' }}>Contact</span>
                </div>
              </>
            ) : (
              <>
                <div style={BANNER_STYLES.customerName}>Unknown Caller</div>
                <div style={BANNER_STYLES.phone}>
                  {call.externalNumberFormatted || call.externalNumber || call.from}
                </div>
              </>
            )}

            {/* Answered by someone else */}
            {answeredByOther && (
              <div style={{ fontSize: 12, opacity: 0.7, marginTop: 4, fontStyle: 'italic' }}>
                Answered by {call.answeredByUsername || 'another user'}
              </div>
            )}

            {/* Duration for ended calls */}
            {call.duration && (
              <div style={BANNER_STYLES.duration}>
                Duration: {formatDuration(call.duration)}
              </div>
            )}

            {/* Voicemail transcription */}
            {isVoicemail && call.voicemailTranscription && (
              <div style={BANNER_STYLES.voicemailText}>
                "{call.voicemailTranscription}"
              </div>
            )}

            {/* Action buttons — only shown when a customer is resolved */}
            {showActions && customer && (() => {
              // Collect all account numbers linked to this phone for multi-account views
              const allAccounts = [...new Set([
                ...customers.map(c => c.accountNumber),
                call.matchedCustomer?.accountNumber,
              ].filter(Boolean))].join(',');

              return (
              <div style={BANNER_STYLES.actions}>
                {onNewOrder && (
                  <button
                    style={BANNER_STYLES.actionBtn(isDark)}
                    onClick={() => onNewOrder(customer.accountNumber)}
                    title="Open Order Entry for this customer"
                  >
                    📋 New Order
                  </button>
                )}
                {onSalesLedger && (
                  <button
                    style={BANNER_STYLES.actionBtn(isDark)}
                    onClick={() => onSalesLedger(allAccounts)}
                    title="Open Sales Ledger for all linked accounts"
                  >
                    📒 Sales Ledger
                  </button>
                )}
                {onSalesActivity && (
                  <button
                    style={BANNER_STYLES.actionBtn(isDark)}
                    onClick={() => onSalesActivity(allAccounts)}
                    title="Open Sales Activity for all linked accounts"
                  >
                    📊 Sales Activity
                  </button>
                )}
                {onPayments && (
                  <button
                    style={BANNER_STYLES.actionBtn(isDark)}
                    onClick={() => onPayments(allAccounts)}
                    title="Open Customer Payments for all linked accounts"
                  >
                    💳 Payments
                  </button>
                )}
              </div>
              );
            })()}

            {/* Unknown caller (no matches at all) — search + save contact buttons */}
            {!customer && !call.contactName && !hasMultiple && (() => {
              const contactState = savingContact[call.callId];
              const phoneForSave = call.externalNumber || call.from;
              return (
                <div style={{ ...BANNER_STYLES.actions, flexDirection: 'column', gap: 6 }}>
                  {/* Link to existing customer */}
                  {onSearchCustomer && (
                    <button
                      style={BANNER_STYLES.actionBtn(isDark)}
                      onClick={() =>
                        onSearchCustomer(
                          phoneForSave,
                          call.callId,
                          handleLinkCustomer
                        )
                      }
                      title="Search for a customer to link this number to"
                    >
                      🔍 Link to Customer
                    </button>
                  )}
                  {/* Save as named contact (no account) */}
                  {!contactState || contactState === 'input' ? (
                    <div style={{ display: 'flex', gap: 4, alignItems: 'center', width: '100%' }}>
                      <input
                        type="text"
                        placeholder="Name (e.g. DPD Driver)"
                        value={contactNameInput[call.callId] || ''}
                        onChange={(e) => setContactNameInput((prev) => ({ ...prev, [call.callId]: e.target.value }))}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleSaveContact(call.callId, phoneForSave); }}
                        style={{
                          flex: 1,
                          padding: '5px 8px',
                          borderRadius: 6,
                          border: `1px solid ${isDark ? '#475569' : '#cbd5e1'}`,
                          background: isDark ? '#1e293b' : '#fff',
                          color: isDark ? '#e2e8f0' : '#1e293b',
                          fontSize: 12,
                          outline: 'none',
                        }}
                      />
                      <button
                        style={{ ...BANNER_STYLES.actionBtn(isDark), whiteSpace: 'nowrap' }}
                        onClick={() => handleSaveContact(call.callId, phoneForSave)}
                        disabled={!(contactNameInput[call.callId] || '').trim()}
                        title="Save this number as a named contact"
                      >
                        💾 Save Contact
                      </button>
                    </div>
                  ) : contactState === 'saving' ? (
                    <span style={{ fontSize: 12, opacity: 0.6 }}>Saving…</span>
                  ) : (
                    <span style={{ fontSize: 12, color: '#22c55e', fontWeight: 500 }}>✓ Contact saved</span>
                  )}
                </div>
              );
            })()}
          </div>
        );
      })}

      {hiddenCount > 0 && (
        <div style={{ ...BANNER_STYLES.counter, pointerEvents: 'auto', color: isDark ? '#94a3b8' : '#64748b' }}>
          +{hiddenCount} more call{hiddenCount > 1 ? 's' : ''}
        </div>
      )}
    </div>
  );
}
